//---------------------------------------------------------
// Soft F3/F4 — Provision wizard (NAT one-arm, single or HA pair) + job poll + catalog Autocomplete
//---------------------------------------------------------
import { Box, Button, FormControlLabel, Switch, Tooltip } from "@mui/material";
import {
  ASSIGNED_AT_CREATE,
  HA_READY,
  HA_SWITCH_LABEL,
  haJobFailure,
  haPreview,
  haSwitchState,
  networkModeOption,
  NOT_AVAILABLE_YET,
  provisionMode,
  visibleSteps,
} from "components/soft/ha/haCopy";
import {
  SoftAlert,
  SoftAutocomplete,
  SoftCard,
  SoftLabelWithId,
  SoftMono,
  SoftPage,
  SoftPreviewRow,
  SoftRunText,
  SoftSectionTitle,
  SoftSelect,
  SoftStack,
  SoftText,
  SoftTextField,
} from "components/soft/SoftUI";
import { SOFT } from "components/soft/softTheme";
import { SOFT_SELECT_PLACEHOLDER } from "components/soft/softField";
import {
  createProvisionJob,
  getProvisionJob,
  listLinodeAccounts,
  listSSHKeys,
  listLinodeCatalogFirewalls,
  listLinodeCatalogImages,
  listLinodeCatalogPrivateImages,
  listLinodeCatalogRegions,
  listLinodeCatalogTypes,
  listSoftTemplates,
} from "connector/oam/softProvisionApi";
import { useRole } from "hooks/query/oamHooks";
import { LINODE_PAUSED_TOOLTIP, useLinodePaused } from "hooks/useLinodeMutate";
import { t } from "i18next";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import {
  SOFT_DEFAULT_OS,
  SOFT_DEFAULT_PLAN,
  SOFT_DEFAULT_REGION,
  SOFT_NETWORK_MODE_LEGACY,
  SOFT_NETWORK_MODES,
  SOFT_OS_VARIANTS,
  SOFT_PLAN_TYPES,
  SOFT_REGIONS,
  SoftNetworkMode,
  SoftCatalogFirewall,
  SoftCatalogImage,
  SoftCatalogRegion,
  SoftCatalogType,
  SoftLinodeAccount,
  SoftProvisionJob,
  SoftSSHKey,
  SoftProvisionJobCreateRequest,
  SoftTemplate,
} from "types/soft_provision";
import {
  NO_ACCOUNT_COPY,
  NO_KEY_COPY,
  accountOptionText,
  initialProvisionPick,
  previewLabels,
  provisionStartDisabled,
  provisionSubmitPick,
  sshKeyChoicesForAccount,
  sshKeyOptionText,
} from "./linodeUserKeys";
import {
  CATALOG_SEP,
  joinIdName,
  matchesIdOrName,
  planSpec,
  planTooSmall,
  regionName as catalogRegionName,
  sortPlans,
  sortRegions,
  FALLBACK_REGIONS,
  FALLBACK_TYPES,
  withFallback,
} from "./catalogLabels";
import { currentControlPlaneCidr, firewallPreviewLines } from "./controlPlane";

type FirewallChoice =
  | { kind: "existing"; firewall: SoftCatalogFirewall }
  | { kind: "new"; label: string }
  | null;

type ImagePick = {
  id: string;
  label: string;
  group: "OS" | "Private images";
  kind: "os" | "private";
  name?: string;
  placeholder?: boolean;
};

function pickDefaultOS(images: SoftCatalogImage[]): string {
  const debian = images.find(
    (i) => i.id === SOFT_DEFAULT_OS || i.id === "linode/debian13",
  );
  if (debian) return debian.id;
  if (images[0]) return images[0].id;
  return SOFT_DEFAULT_OS;
}

function optionLabel(id: string, label?: string): string {
  if (label && label !== id) return `${label} (${id})`;
  return id;
}

export default function ProvisionWizardPage() {
  const { is_admin } = useRole();
  const linodePaused = useLinodePaused();
  const cpCidr = useMemo(() => currentControlPlaneCidr(), []);
  const [allTemplates, setAllTemplates] = useState<SoftTemplate[]>([]);
  // HA is derived from the mode (Q3); the request never carries an ha field.
  const [ha, setHA] = useState(false);
  const [family, setFamily] = useState<'nat' | 'dsr'>('nat');
  const wireMode = provisionMode(ha, family);
  const templates = useMemo(
    () => allTemplates.filter((x) => x.mode === wireMode),
    [allTemplates, wireMode],
  );
  const [templateId, setTemplateId] = useState("");
  const [accounts, setAccounts] = useState<SoftLinodeAccount[]>([]);
  const [accountId, setAccountId] = useState(initialProvisionPick().accountId);
  const [sshKeys, setSSHKeys] = useState<SoftSSHKey[]>([]);
  const [sshKeyId, setSSHKeyId] = useState(initialProvisionPick().sshKeyId);
  const [types, setTypes] = useState<SoftCatalogType[]>([]);
  const [regions, setRegions] = useState<SoftCatalogRegion[]>([]);
  const catalogSeq = useRef(0);
  const [images, setImages] = useState<SoftCatalogImage[]>([]);
  const [privateImages, setPrivateImages] = useState<SoftCatalogImage[]>([]);
  const [privateImagesLoaded, setPrivateImagesLoaded] = useState(false);
  const [firewalls, setFirewalls] = useState<SoftCatalogFirewall[]>([]);
  const [catalogErr, setCatalogErr] = useState<string | null>(null);
  const [osVariant, setOsVariant] = useState(SOFT_DEFAULT_OS);
  const [imageId, setImageId] = useState("");
  const [region, setRegion] = useState(SOFT_DEFAULT_REGION);
  const [label, setLabel] = useState("");
  const [planType, setPlanType] = useState(SOFT_DEFAULT_PLAN);
  const [networkMode, setNetworkMode] = useState<SoftNetworkMode>(
    SOFT_NETWORK_MODE_LEGACY,
  );
  const [vlanLabel, setVlanLabel] = useState("loxilb");
  const [vlanAddress, setVlanAddress] = useState("192.168.200.10/24");
  const [subnetId, setSubnetId] = useState("");
  const [firewallChoice, setFirewallChoice] = useState<FirewallChoice>(null);
  const [firewallInput, setFirewallInput] = useState("");
  const [job, setJob] = useState<SoftProvisionJob | undefined>();
  const [warnings, setWarnings] = useState<string[]>([]);
  const [err, setErr] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const pollRef = useRef<number | null>(null);

  const refreshTemplates = useCallback(async () => {
    const list = await listSoftTemplates();
    setAllTemplates(list);
  }, []);

  useEffect(() => {
    setTemplateId((prev) =>
      prev && templates.some((tm) => tm.id === prev)
        ? prev
        : templates[0]?.id || "",
    );
  }, [templates]);

  const refreshAccounts = useCallback(async () => {
    const list = await listLinodeAccounts();
    setAccounts(list);
    setAccountId((prev) =>
      prev && list.some((a) => a.id === prev)
        ? prev
        : initialProvisionPick().accountId,
    );
  }, []);

  const refreshKeys = useCallback(async () => {
    const list = await listSSHKeys();
    setSSHKeys(list);
    setSSHKeyId((prev) =>
      prev && list.some((k) => k.id === prev)
        ? prev
        : initialProvisionPick().sshKeyId,
    );
  }, []);

  const refreshCatalog = useCallback(async (acct: string) => {
    setCatalogErr(null);
    setPrivateImagesLoaded(false);
    if (!acct) {
      setTypes([]);
      setRegions([]);
      setImages([]);
      setPrivateImages([]);
      setPrivateImagesLoaded(false);
      setFirewalls([]);
      setFirewallChoice(null);
      setFirewallInput("");
      setImageId("");
      setCatalogErr("Select a Linode account to load the catalog");
      return;
    }
    const seq = ++catalogSeq.current;
    try {
      // allSettled: one failing list (e.g. firewalls 422) must not blank the others.
      const settled = await Promise.allSettled([
        listLinodeCatalogTypes(acct),
        listLinodeCatalogRegions(acct),
        listLinodeCatalogImages(acct),
        listLinodeCatalogPrivateImages(acct),
        listLinodeCatalogFirewalls(acct),
      ]);
      if (seq !== catalogSeq.current) return; // a newer account pick won
      const val = <T,>(r: PromiseSettledResult<T[]>): T[] =>
        r.status === "fulfilled" && Array.isArray(r.value) ? r.value : [];
      const tList = val(settled[0] as PromiseSettledResult<SoftCatalogType[]>);
      const rList = val(settled[1] as PromiseSettledResult<SoftCatalogRegion[]>);
      const iList = val(settled[2] as PromiseSettledResult<any[]>);
      const pList = val(settled[3] as PromiseSettledResult<any[]>);
      const fList = val(settled[4] as PromiseSettledResult<any[]>);
      const firstErr = settled.find((r) => r.status === "rejected") as
        | PromiseRejectedResult
        | undefined;
      if (firstErr) setCatalogErr(firstErr.reason?.message || "catalog load failed");
      setTypes(tList);
      setRegions(rList);
      setImages(iList);
      setPrivateImages(pList);
      setPrivateImagesLoaded(true);
      setFirewalls(fList);
      setFirewallChoice(null);
      setFirewallInput("");
      setImageId("");

      // CoS preselects (do not hide): keep defaults when present; else first catalog item.
      if (tList.length) {
        const allowed = tList.filter((x) => !planTooSmall(x));
        const pref = allowed.find((x) => x.id === SOFT_DEFAULT_PLAN) || allowed[0];
        if (pref) setPlanType(pref.id);
      }
      if (rList.length) {
        const pref = rList.find((x) => x.id === SOFT_DEFAULT_REGION);
        setRegion(pref?.id || rList[0].id);
      }
      if (iList.length) {
        setOsVariant(pickDefaultOS(iList));
      }
      if (!tList.length && !rList.length && !iList.length) {
        setCatalogErr(
          "Catalog empty — using Soft-side fallback labels (configure Linode account / check Metadata + cloud-init filters)",
        );
      }
    } catch (e: any) {
      setCatalogErr(e?.message || "catalog load failed");
    }
  }, []);

  useEffect(() => {
    if (is_admin) {
      refreshTemplates();
      refreshAccounts();
      refreshKeys();
    }
    return () => {
      if (pollRef.current) window.clearInterval(pollRef.current);
    };
  }, [is_admin, refreshTemplates, refreshAccounts, refreshKeys]);

  useEffect(() => {
    if (is_admin && accountId) refreshCatalog(accountId);
    if (is_admin && !accountId) {
      setPrivateImages([]);
      setPrivateImagesLoaded(false);
      setFirewalls([]);
    }
  }, [is_admin, accountId, refreshCatalog]);

  const selectedAccount = accounts.find((a) => a.id === accountId);
  const keyChoices = sshKeyChoicesForAccount(sshKeys, accountId);
  const selectedKey = keyChoices.find((k) => k.id === sshKeyId);
  const preview = previewLabels(selectedAccount, selectedKey);
  const typeList = withFallback(types, FALLBACK_TYPES, SOFT_PLAN_TYPES);
  const regionList = withFallback(regions, FALLBACK_REGIONS, SOFT_REGIONS);
  const planOptions = sortPlans(typeList).map((x) => x.id);
  const regionOptions = sortRegions(regionList).map((x) => x.id);
  const osIds = images.length ? images.map((x) => x.id) : [...SOFT_OS_VARIANTS];

  const imageOptions = useMemo((): ImagePick[] => {
    const osPicks: ImagePick[] = osIds.map((id) => ({
      id,
      label: optionLabel(id, images.find((x) => x.id === id)?.label),
      name: images.find((x) => x.id === id)?.label,
      group: "OS",
      kind: "os",
    }));
    let privatePicks: ImagePick[];
    if (!accountId) {
      privatePicks = [
        {
          id: "__need_account",
          label: "Choose an account first.",
          group: "Private images",
          kind: "private",
          placeholder: true,
        },
      ];
    } else if (privateImagesLoaded && privateImages.length === 0) {
      privatePicks = [
        {
          id: "__empty_private",
          label: "No private images.",
          group: "Private images",
          kind: "private",
          placeholder: true,
        },
      ];
    } else {
      privatePicks = privateImages.map((img) => ({
        id: img.id,
        label: optionLabel(img.id, img.label),
        name: img.label,
        group: "Private images",
        kind: "private",
      }));
    }
    return [...osPicks, ...privatePicks];
  }, [accountId, images, osIds, privateImages, privateImagesLoaded]);

  const selectedImagePick = useMemo(() => {
    if (imageId) {
      return (
        imageOptions.find(
          (o) => o.kind === "private" && o.id === imageId && !o.placeholder,
        ) || null
      );
    }
    return (
      imageOptions.find((o) => o.kind === "os" && o.id === osVariant) || null
    );
  }, [imageId, imageOptions, osVariant]);

  const planName = (id: string) => planSpec(typeList.find((x) => x.id === id));
  const regionName = (id: string) =>
    catalogRegionName(regionList.find((x) => x.id === id));
  const planLabel = (id: string) => joinIdName(id, planName(id));
  const regionLabel = (id: string) => joinIdName(id, regionName(id));
  const filterBy =
    (nameOf: (id: string) => string | undefined) =>
    (opts: string[], st: { inputValue: string }) =>
      opts.filter((id) => matchesIdOrName(st.inputValue, id, nameOf(id)));
  const renderIdName = (id: string, name?: string) => (
    <span>
      <Box component="span" sx={{ fontFamily: SOFT.font.mono }}>
        {id}
      </Box>
      {name ? `${CATALOG_SEP}${name}` : null}
    </span>
  );

  const imagePreviewId = selectedImagePick?.id || imageId || osVariant;
  const imagePreviewName = (
    selectedImagePick?.kind === "private" ? privateImages : images
  ).find((x) => x.id === imagePreviewId)?.label;

  const firewallPreview = firewallPreviewLines(firewallChoice, cpCidr);
  const firewallReady =
    firewallChoice !== null &&
    (firewallChoice.kind === "existing" ||
      firewallChoice.label.trim().length > 0);
  const imageReady = Boolean(imageId) || Boolean(osVariant);

  const startPoll = (jobId: string) => {
    if (pollRef.current) window.clearInterval(pollRef.current);
    pollRef.current = window.setInterval(async () => {
      const j = await getProvisionJob(jobId);
      if (!j) return;
      setJob(j);
      if (j.status === "succeeded" || j.status === "failed") {
        if (pollRef.current) window.clearInterval(pollRef.current);
        pollRef.current = null;
      }
    }, 2000);
  };

  const onSubmit = async () => {
    setBusy(true);
    setErr(null);
    setWarnings([]);
    setJob(undefined);
    if (accounts.length === 0) {
      setBusy(false);
      setErr(NO_ACCOUNT_COPY);
      return;
    }
    if (sshKeys.length === 0) {
      setBusy(false);
      setErr(NO_KEY_COPY);
      return;
    }
    if (!accountId || !sshKeyId) {
      setBusy(false);
      setErr(t("Select a Linode account and an SSH key"));
      return;
    }
    if (planTooSmall(typeList.find((x) => x.id === planType))) {
      setBusy(false);
      setErr(t("LoxiLB needs a plan with 16 GB of memory or more"));
      return;
    }
    if (
      !firewallChoice ||
      (firewallChoice.kind === "new" && !firewallChoice.label.trim())
    ) {
      setBusy(false);
      setErr(t("Select or enter a firewall"));
      return;
    }
    const pick = provisionSubmitPick(accountId, sshKeyId);
    const body: SoftProvisionJobCreateRequest = {
      templateId,
      region,
      mode: wireMode,
      accountId: pick.accountId,
      sshKeyId: pick.sshKeyId,
      label: label || undefined,
      planType,
      networkMode,
    };
    if (networkMode === "nat_vlan" || networkMode === "dsr_vlan") {
      body.vlan = {label: vlanLabel.trim(), address: vlanAddress.trim()};
    }
    if (networkMode === "nat_vpc") {
      body.vpc = {subnetId: Number(subnetId)};
    }
    if (imageId) {
      body.imageId = imageId;
    } else {
      body.osVariant = osVariant;
    }
    if (firewallChoice.kind === "existing") {
      body.firewallId = firewallChoice.firewall.id;
    } else {
      body.firewallLabel = firewallChoice.label.trim();
    }
    const idem = crypto.randomUUID?.() || `soft-${Date.now()}`;
    const res = await createProvisionJob(body, idem);
    setBusy(false);
    if (!res.ok || !res.data) {
      if (res.code === "ACCOUNT_MISSING") {
        setErr(t("That account is gone. Choose another one."));
      } else if (res.code === "LINODE_MUTATE_DISABLED") {
        setErr(
          t(
            "Paused for a security fix. Linode changes are turned off for now.",
          ),
        );
      } else {
        setErr(res.error || "create job failed");
      }
      return;
    }
    setJob(res.data as SoftProvisionJob);
    const w = (res.data as SoftProvisionJob).warnings;
    if (w?.length) setWarnings(w);
    const jobId =
      (res.data as any).jobId || (res.data as SoftProvisionJob).jobId;
    if (jobId) startPoll(jobId);
  };

  if (!is_admin) {
    return (
      <SoftPage>
        <SoftAlert severity="error">
          {t("You need an admin account for this page.")}
        </SoftAlert>
      </SoftPage>
    );
  }

  const selectedTemplate = templates.find((tm) => tm.id === templateId);
  const haSwitch = haSwitchState(networkMode);
  const haPrev = haPreview(networkMode, ha);
  const haFail = job?.ha ? haJobFailure(job) : null;
  const firewallValue =
    firewallChoice?.kind === "existing"
      ? firewallChoice.firewall
      : firewallChoice?.kind === "new"
        ? firewallChoice.label
        : null;

  return (
    <SoftPage title={t("Provision LoxiLB")}>
      <Box
        sx={{
          display: "flex",
          flexDirection: { xs: "column", lg: "row" },
          alignItems: "flex-start",
          gap: "32px",
        }}
      >
        <SoftStack maxWidth={720}>
          {catalogErr && <SoftAlert severity="warning">{catalogErr}</SoftAlert>}
          {err && <SoftAlert severity="error">{err}</SoftAlert>}
          {warnings.map((w, i) => (
            <SoftAlert key={i} severity="warning">
              {w}
            </SoftAlert>
          ))}
          <SoftSelect
            id="soft-provision-account"
            label={t("Linode account")}
            value={accountId}
            onChange={setAccountId}
            placeholder={SOFT_SELECT_PLACEHOLDER.account}
            helper={t("The account that creates this load balancer.")}
            options={accounts.map((a) => ({
              value: a.id,
              label: accountOptionText(a),
            }))}
          />
          {accounts.length === 0 ? (
            <SoftAlert severity="warning">{NO_ACCOUNT_COPY}</SoftAlert>
          ) : null}
          <SoftSelect
            id="soft-provision-ssh-key"
            label={t("SSH key")}
            value={sshKeyId}
            onChange={setSSHKeyId}
            placeholder={SOFT_SELECT_PLACEHOLDER.sshKey}
            helper={t("The one public key installed on this load balancer.")}
            options={keyChoices.map((k) => ({
              value: k.id,
              label: sshKeyOptionText(k),
            }))}
          />
          {sshKeys.length === 0 ? (
            <SoftAlert severity="warning">{NO_KEY_COPY}</SoftAlert>
          ) : null}
          <SoftSelect
            id="soft-provision-template"
            label={t("Template")}
            value={templateId}
            onChange={setTemplateId}
            placeholder={SOFT_SELECT_PLACEHOLDER.template}
            options={templates.map((tm) => ({ value: tm.id, label: tm.name }))}
          />
          <SoftAutocomplete<ImagePick>
            id="soft-provision-image"
            label={t("OS / image")}
            options={imageOptions}
            value={selectedImagePick}
            groupBy={(opt) => opt.group}
            getOptionLabel={(opt) => opt.label}
            isOptionEqualToValue={(a, b) => a.id === b.id && a.kind === b.kind}
            getOptionDisabled={(opt) => Boolean(opt.placeholder)}
            renderOption={(props, opt) => {
              const { key, ...liProps } = props as typeof props & {
                key: string;
              };
              return (
                <li key={key} {...liProps}>
                  {opt.placeholder ? (
                    opt.label
                  ) : (
                    <SoftLabelWithId label={opt.name} id={opt.id} />
                  )}
                </li>
              );
            }}
            onChange={(_, v) => {
              if (!v || v.placeholder) return;
              if (v.kind === "private") {
                setImageId(v.id);
              } else {
                setImageId("");
                setOsVariant(v.id);
              }
            }}
          />
          <SoftAutocomplete<SoftCatalogFirewall | string, true>
            id="soft-provision-firewall"
            label={t("Firewall")}
            helper={t(
              "Pick an existing firewall or type a label for a new one.",
            )}
            freeSolo
            options={firewalls}
            value={firewallValue}
            inputValue={firewallInput}
            onInputChange={(_, v) => {
              setFirewallInput(v);
            }}
            getOptionLabel={(opt) =>
              typeof opt === "string" ? opt : opt.label
            }
            isOptionEqualToValue={(a, b) => {
              if (typeof a === "string" || typeof b === "string")
                return a === b;
              return a.id === b.id;
            }}
            onChange={(_, v) => {
              if (v == null) {
                setFirewallChoice(null);
                setFirewallInput("");
                return;
              }
              if (typeof v === "string") {
                const trimmed = v.trim();
                if (!trimmed) {
                  setFirewallChoice(null);
                  setFirewallInput("");
                  return;
                }
                const match = firewalls.find((f) => f.label === trimmed);
                if (match) {
                  setFirewallChoice({ kind: "existing", firewall: match });
                  setFirewallInput(match.label);
                } else {
                  setFirewallChoice({ kind: "new", label: trimmed });
                  setFirewallInput(trimmed);
                }
                return;
              }
              setFirewallChoice({ kind: "existing", firewall: v });
              setFirewallInput(v.label);
            }}
            onBlur={() => {
              const trimmed = firewallInput.trim();
              if (!trimmed) {
                setFirewallChoice(null);
                return;
              }
              const match = firewalls.find((f) => f.label === trimmed);
              if (match) {
                setFirewallChoice({ kind: "existing", firewall: match });
                setFirewallInput(match.label);
              } else {
                setFirewallChoice({ kind: "new", label: trimmed });
              }
            }}
          />
          <SoftAutocomplete<string>
            id="soft-provision-region"
            label={t("Region")}
            options={regionOptions}
            value={region}
            onChange={(_, v) => setRegion(v || SOFT_DEFAULT_REGION)}
            getOptionLabel={regionLabel}
            filterOptions={filterBy(regionName)}
            renderOption={(props, id) => {
              const { key, ...liProps } = props as typeof props & {
                key: string;
              };
              return (
                <li key={key} {...liProps}>
                  {renderIdName(id, regionName(id))}
                </li>
              );
            }}
          />
          <SoftAutocomplete<string>
            id="soft-provision-plan"
            label={t("Plan type")}
            options={planOptions}
            value={planType}
            onChange={(_, v) => {
              if (v && planTooSmall(typeList.find((x) => x.id === v))) return;
              setPlanType(v || SOFT_DEFAULT_PLAN);
            }}
            getOptionLabel={planLabel}
            getOptionDisabled={(id) => planTooSmall(typeList.find((x) => x.id === id))}
            filterOptions={filterBy(planName)}
            renderOption={(props, id) => {
              const { key, ...liProps } = props as typeof props & {
                key: string;
              };
              const small = planTooSmall(typeList.find((x) => x.id === id));
              return (
                <li key={key} {...liProps} style={{...liProps.style, color: small ? "#9aa0a6" : undefined}}>
                  {renderIdName(id, planName(id))}
                  {small ? ` \u2014 ${t("Needs 16 GB")}` : ""}
                </li>
              );
            }}
          />
          <SoftSelect
            id="soft-provision-family"
            label={t("Load balancer mode")}
            value={family}
            onChange={(v) => {
              const next = v === "dsr" ? "dsr" : "nat";
              setFamily(next);
              const current = SOFT_NETWORK_MODES.find((m) => m.id === networkMode);
              if (!current || current.family !== next) {
                const fallback = SOFT_NETWORK_MODES.find((m) => m.family === next);
                if (fallback) setNetworkMode(fallback.id);
              }
            }}
            options={[
              {value: "nat", label: "NAT"},
              {value: "dsr", label: "DR"},
            ]}
          />
          <SoftSelect
            id="soft-provision-network-mode"
            label={t("Network mode")}
            value={networkMode}
            onChange={(v) => {
              const m = networkModeOption(v);
              if (!m || !m.enabled) return; // a disabled pick never becomes the value
              setNetworkMode(v as SoftNetworkMode);
              if (haSwitchState(v).disabled) setHA(false);
            }}
            options={SOFT_NETWORK_MODES.filter((m) => m.family === family).map((m) => ({
              value: m.id,
              disabled: m.disabled,
              label: m.disabled ? (
                <Box
                  component="span"
                  sx={{ display: "flex", gap: "8px", alignItems: "baseline" }}
                >
                  <span>{m.label}</span>
                  <Box
                    component="span"
                    sx={{ ...SOFT.type.helper, color: SOFT.color.textMuted }}
                  >
                    {NOT_AVAILABLE_YET}
                  </Box>
                </Box>
              ) : (
                m.label
              ),
            }))}
          />
          <Box>
            <FormControlLabel
              control={
                <Switch
                  checked={ha}
                  onChange={(e) => setHA(e.target.checked)}
                  disabled={haSwitch.disabled}
                  inputProps={{
                    "aria-describedby": "soft-provision-ha-helper",
                  }}
                  data-testid="soft-provision-ha"
                />
              }
              label={HA_SWITCH_LABEL}
            />
            <Box
              id="soft-provision-ha-helper"
              sx={{ ...SOFT.type.helper, color: SOFT.color.textMuted }}
            >
              {haSwitch.helper}
            </Box>
          </Box>
          {(networkMode === "nat_vlan" || networkMode === "dsr_vlan") && (
            <>
              <SoftTextField
                id="soft-provision-vlan"
                label={t("VLAN")}
                value={vlanLabel}
                onChange={(e) => setVlanLabel(e.target.value)}
              />
              <SoftTextField
                id="soft-provision-vlan-address"
                label={t("Load balancer's VLAN address")}
                value={vlanAddress}
                onChange={(e) => setVlanAddress(e.target.value)}
              />
            </>
          )}
          {networkMode === "nat_vpc" && (
            <SoftTextField
              id="soft-provision-subnet"
              label={t("Subnet ID")}
              value={subnetId}
              onChange={(e) => setSubnetId(e.target.value)}
            />
          )}
          <SoftTextField
            id="soft-provision-label"
            label={t("Label (optional)")}
            value={label}
            onChange={(e) => setLabel(e.target.value)}
          />
          {job && (
            <SoftCard>
              <Box sx={{ ...SOFT.type.sectionTitle }}>
                {t("Job")} <SoftMono>{job.jobId}</SoftMono>
                {" — "}
                {job.status}
              </Box>
              <Box
                sx={{
                  display: "flex",
                  flexDirection: "column",
                  gap: "4px",
                  mt: "12px",
                }}
              >
                {job.label ? (
                  <SoftPreviewRow label={t("Label")} value={job.label} />
                ) : null}
                {job.planType ? (
                  <SoftPreviewRow
                    label={t("Plan type")}
                    value={job.planType}
                    mono
                  />
                ) : null}
                {job.publicIp ? (
                  <SoftPreviewRow
                    label={t("Public IP")}
                    value={job.publicIp}
                    mono
                  />
                ) : null}
                {job.privateIp ? (
                  <SoftPreviewRow
                    label={t("Private IP")}
                    value={job.privateIp}
                    mono
                  />
                ) : null}
                {job.instanceId != null ? (
                  <SoftPreviewRow
                    label={t("ID")}
                    value={String(job.instanceId)}
                    mono
                  />
                ) : null}
              </Box>
              {job.ha && (
                <Box
                  sx={{
                    display: "flex",
                    flexDirection: "column",
                    gap: "4px",
                    mt: "12px",
                  }}
                  data-testid="ha-job-steps"
                >
                  {visibleSteps(job.status, job.steps).map((s) => (
                    <Box
                      key={s.label}
                      data-state={s.state}
                      sx={{
                        ...SOFT.type.value,
                        color:
                          s.state === "failed"
                            ? SOFT.color.error
                            : s.state === "done"
                              ? SOFT.color.text
                              : SOFT.color.textMuted,
                      }}
                    >
                      {s.state === "done"
                        ? "✓ "
                        : s.state === "failed"
                          ? "✕ "
                          : s.state === "running"
                            ? "… "
                            : "○ "}
                      {s.label}
                    </Box>
                  ))}
                </Box>
              )}
              {job.ha && haFail && (
                <SoftAlert
                  severity="error"
                  sx={{ mt: "20px" }}
                  data-testid="ha-job-failure"
                >
                  {haFail}
                </SoftAlert>
              )}
              {job.ha && job.status === "succeeded" && (
                <SoftAlert severity="success" sx={{ mt: "20px" }}>
                  {HA_READY}
                </SoftAlert>
              )}
              {!job.ha && job.error && (
                <SoftAlert severity="error" sx={{ mt: "20px" }}>
                  {job.error.code === "ACCOUNT_MISSING"
                    ? t("That account is gone. Choose another one.")
                    : job.error.code === "LINODE_MUTATE_DISABLED"
                      ? t(
                          "Paused for a security fix. Linode changes are turned off for now.",
                        )
                      : job.error.message}
                </SoftAlert>
              )}
              {!job.ha && job.status === "succeeded" && (
                <SoftAlert severity="success" sx={{ mt: "20px" }}>
                  {t("This load balancer is ready. It's on Instances.")}
                </SoftAlert>
              )}
            </SoftCard>
          )}
        </SoftStack>
        <Box
          sx={{
            width: { xs: "100%", lg: 560 },
            flex: { lg: "0 0 560px" },
            position: { xs: "static", lg: "sticky" },
            top: 0,
            alignSelf: "flex-start",
          }}
        >
          <SoftCard>
            <SoftSectionTitle>{t("Preview")}</SoftSectionTitle>
            <Box mt="8px">
              {haPrev.sentences.map((line) => (
                <Box key={line} data-testid="mode-sentence">
                  <SoftText>{line}</SoftText>
                </Box>
              ))}
            </Box>
            <Box
              sx={{
                display: "flex",
                flexDirection: "column",
                gap: "8px",
                mt: "16px",
              }}
            >
              <SoftPreviewRow
                label={t("Linode account")}
                value={preview.account || t("None")}
              />
              <SoftPreviewRow
                label={t("SSH key")}
                value={preview.key || t("None")}
              />
              <SoftPreviewRow
                label={t("Template")}
                value={
                  <>
                    {selectedTemplate?.name || t("None")}
                    {templateId ? (
                      <Box component="span" sx={{ whiteSpace: "nowrap" }}>
                        {" "}
                        <SoftMono>{templateId}</SoftMono>
                      </Box>
                    ) : null}
                  </>
                }
              />
              <SoftPreviewRow
                label={t("OS / image")}
                value={
                  <SoftLabelWithId
                    label={imagePreviewName}
                    id={imagePreviewId}
                  />
                }
              />
              <SoftPreviewRow
                label={t("Firewall")}
                value={
                  firewallChoice?.kind === "existing"
                    ? firewallChoice.firewall.label
                    : firewallChoice?.kind === "new"
                      ? firewallChoice.label
                      : t("None")
                }
              />
              {firewallPreview.length || haPrev.extraFirewallLines.length ? (
                <Box
                  sx={{
                    ...SOFT.type.helper,
                    color: SOFT.color.textMuted,
                    display: "flex",
                    flexDirection: "column",
                    gap: "2px",
                  }}
                >
                  {[...firewallPreview, ...haPrev.extraFirewallLines].map(
                    (line) => (
                      <Box key={line} data-testid="firewall-preview-line">
                        <SoftRunText text={line} />
                      </Box>
                    ),
                  )}
                </Box>
              ) : null}
              <SoftPreviewRow
                label={t("Region")}
                value={
                  <SoftLabelWithId label={regionName(region)} id={region} />
                }
              />
              <SoftPreviewRow
                label={t("Plan")}
                value={
                  <SoftLabelWithId label={planName(planType)} id={planType} />
                }
              />
              <SoftPreviewRow
                label={t("Network mode")}
                value={
                  SOFT_NETWORK_MODES.find((m) => m.id === networkMode)?.label ||
                  networkMode
                }
              />
              <SoftPreviewRow
                label={t("Label")}
                value={label.trim() ? label.trim() : t("None")}
              />
              {haPrev.vip ? (
                <SoftPreviewRow label={t("VIP")} value={haPrev.vip} />
              ) : null}
              {haPrev.nodes.map((n) => (
                <Box
                  key={n.title}
                  data-testid="ha-node-preview"
                  sx={{
                    display: "flex",
                    flexDirection: "column",
                    gap: "4px",
                    mt: "4px",
                  }}
                >
                  <Box sx={{ ...SOFT.type.label, color: SOFT.color.text }}>
                    {n.title}
                  </Box>
                  <SoftPreviewRow
                    label={t("Private address")}
                    value={
                      n.privateAddress === ASSIGNED_AT_CREATE
                        ? ASSIGNED_AT_CREATE
                        : n.privateAddress
                    }
                  />
                </Box>
              ))}
            </Box>
            <Box mt="20px">
              <Tooltip
                title={linodePaused ? t(LINODE_PAUSED_TOOLTIP) : ""}
                placement="top"
                arrow
              >
                <span>
                  <Button
                    variant="contained"
                    disabled={
                      linodePaused ||
                      provisionStartDisabled({
                        busy,
                        templateId,
                        accountId,
                        sshKeyId,
                        networkOk:
                          networkModeOption(networkMode)?.enabled === true,
                        firewallReady,
                        imageReady,
                      })
                    }
                    onClick={onSubmit}
                  >
                    {t("Start provision")}
                  </Button>
                </span>
              </Tooltip>
            </Box>
          </SoftCard>
        </Box>
      </Box>
    </SoftPage>
  );
}
